// src/SideMenu.jsx
import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import "./SideMenu.css";
import translations from "./lang";

const Icon = ({ children }) => (
  <span className="sm-icon" aria-hidden="true">{children}</span>
);

export default function SideMenu({
  isOpen,
  onClose,
  isLoggedIn,
  userType,          // 'provider' | 'client' | null
  username,
  profilePicture,
  language,
  onLanguageChange,
  onLogout,
  unreadCount,       // opcionális olvasatlan értesítések száma
}) {
  const t = translations[language] || translations.ro;
  const navigate = useNavigate();

  // 🔥 FELHASZNÁLÓNÉV MEGJELENÍTÉSE (csak @ előtti rész) 🔥
  let displayUsername = username || t.guest;
  if (username && username.includes("@")) {
    displayUsername = username.split("@")[0];
  }

  const DisabledItem = ({ icon, label }) => (
    <div className="sm-item sm-disabled" aria-disabled="true" role="link" tabIndex={-1}>
      <Icon>{icon}</Icon><span>{label}</span>
    </div>
  );

  const roleLabel =
    userType === "provider" ? t.role_provider
    : userType === "client" ? t.role_client
    : "—";

  const year = new Date().getFullYear();
  const copyText = (t.footer_copy || "© {year} MyServiceHub").replace("{year}", year);

  const Badge = ({ value }) =>
    value && Number(value) > 0 ? <span className="sm-badge" aria-label={t.badge_unread || "Unread"}>{value}</span> : null;

  const handleLogout = async () => {
    try {
      if (onLogout) {
        const maybePromise = onLogout();
        if (maybePromise && typeof maybePromise.then === "function") {
          await maybePromise;
        }
      }
    } finally {
      if (onClose) onClose();
      navigate("/Home", { replace: true });
    }
  };

  return (
    <>
      <div className={`sm-overlay ${isOpen ? "open" : ""}`} onClick={onClose} />

      <aside className={`sm-drawer ${isOpen ? "open" : ""}`} aria-hidden={!isOpen} id="side-drawer">
        <div className="sm-header">
          <button className="sm-close" onClick={onClose} aria-label={t.close_menu || "Close menu"}>×</button>

          <div className="sm-user">
            {profilePicture ? (
              <img src={profilePicture} alt="" className="sm-avatar" />
            ) : (
              <div className="sm-avatar placeholder" />
            )}
            <div className="sm-user-info">
              <div className="sm-username">{displayUsername}</div>
              <div className="sm-role">{roleLabel}</div>
            </div>
          </div>
        </div>

        <div className="sm-scroll">
          <div className="sm-section">
            <div className="sm-section-title">{t.nav_title}</div>

            <NavLink to="/Home" className="sm-item" onClick={onClose}>
              <Icon>🏠</Icon><span>{t.nav_home}</span>
            </NavLink>

            <NavLink to="/services" className="sm-item" onClick={onClose}>
              <Icon>🛠️</Icon><span>{t.nav_services}</span>
            </NavLink>

            {isLoggedIn && userType === "provider" && (
              <>
                <NavLink to="/AppointmentsTrai/Appointments" className="sm-item" onClick={onClose}>
                  <Icon>📅</Icon><span>{t.nav_appointments}</span>
                </NavLink>
                <NavLink to="/bookings" className="sm-item" onClick={onClose}>
                  <Icon>🗂️</Icon><span>{t.nav_bookings}</span>
                </NavLink>
                <NavLink to="/statistics" className="sm-item" onClick={onClose}>
                  <Icon>📊</Icon><span>{t.nav_statistics}</span>
                </NavLink>
              </>
            )}

            {isLoggedIn && userType === "client" && (
              <NavLink to="/bookings" className="sm-item" onClick={onClose}>
                <Icon>🗂️</Icon><span>{t.nav_my_bookings}</span>
              </NavLink>
            )}

            {isLoggedIn && (
              <NavLink to="/serviceprofile/profiledata" className="sm-item" onClick={onClose}>
                <Icon>👤</Icon><span>{t.nav_profile}</span>
              </NavLink>
            )}
          </div>

          <div className="sm-section">
            <div className="sm-section-title">{t.extra_title}</div>

            <NavLink to="/notifications" className="sm-item" onClick={onClose}>
              <Icon>🔔</Icon>
              <span>{t.extra_notifications}</span>
              <Badge value={unreadCount} />
            </NavLink>

            <DisabledItem icon="💬" label={t.extra_messages} />
            <DisabledItem icon="⭐" label={t.extra_favorites} />
            <DisabledItem icon="❓" label={t.extra_help} />
            <DisabledItem icon="📄" label={t.extra_privacy} />
            <DisabledItem icon="⚙️" label={t.extra_settings} />
          </div>

          <div className="sm-section">
            <div className="sm-section-title">{t.system_title}</div>
            <label className="sm-item sm-select-wrap">
              <Icon>🌐</Icon>
              <span style={{ flex: 1 }}>{t.system_language}</span>
              <select
                className="sm-select"
                value={language}
                onChange={(e) => onLanguageChange(e.target.value)}
              >
                <option value="ro">RO</option>
                <option value="hu">HU</option>
                <option value="en">EN</option>
              </select>
            </label>
          </div>
        </div>

        <div className="sm-footer">
          {isLoggedIn ? (
            <button className="sm-logout-btn" onClick={handleLogout}>
              🚪 {t.auth_logout}
            </button>
          ) : (
            <NavLink to="/login" className="sm-login-btn" onClick={onClose}>
              🔐 {t.auth_login}
            </NavLink>
          )}
          <div className="sm-copy">{copyText}</div>
        </div>
      </aside>
    </>
  );
}
